import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';

import constants from '../config';
import CartContext from '../contexts/CartContext';
import AuthModalContext from '../contexts/AuthModalContext';
import { resolveCardImage } from '../utils/resolveImage';
import { renderRarityBadge } from '../utils/rarityBadge';

class OrderConfirmation extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    var params = new URLSearchParams(props.location.search);
    var isPaypalReturn = !!(params.get('paypal_type') && params.get('token'));
    this.state = {
      loading: isPaypalReturn,
      error: null,
      paypalOrder: null,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    // Check if this is a PayPal return — PayPal redirects here with token & PayerID
    var params = new URLSearchParams(this.props.location.search);
    var paypalType = params.get('paypal_type');
    var token = params.get('token');

    if (paypalType && token) {
      this.capturePaypalOrder(paypalType, token);
    }
  }

  capturePaypalOrder(paypalType, token) {
    var captureUrl;
    if (paypalType === 'guest') {
      captureUrl = constants.DOMAIN + '/api/v1/transactions/captureguest?token=' + encodeURIComponent(token);
    } else if (paypalType === 'cart') {
      captureUrl = constants.DOMAIN + '/api/v1/transactions/cart/capture?token=' + encodeURIComponent(token);
    } else {
      captureUrl = constants.DOMAIN + '/api/v1/transactions/capture?token=' + encodeURIComponent(token);
    }

    this.setState({ loading: true, error: null });

    fetch(captureUrl)
      .then(function(resp) {
        if (!resp.ok) throw new Error('Capture failed');
        return resp.json();
      })
      .then(function(data) {
        if (data.error) {
          this.setState({ loading: false, error: 'Payment processing failed. Please check your orders.' });
        } else {
          // Merge shipping/payment meta saved before PayPal redirect
          try {
            var orderMeta = JSON.parse(localStorage.getItem('primal_order_meta') || '{}');
            if (orderMeta.shippingAddress) data.shippingAddress = orderMeta.shippingAddress;
            if (orderMeta.shippingTo) data.shippingTo = orderMeta.shippingTo;
            if (orderMeta.paymentInfo) data.paymentInfo = orderMeta.paymentInfo;
            localStorage.removeItem('primal_order_meta');
          } catch (e) {}
          this.setState({ loading: false, paypalOrder: data });
          // Clear cart after successful cart PayPal capture
          if (data.cartPurchase && this.context && this.context.clearCart) {
            this.context.clearCart();
          }
          localStorage.removeItem('primal_cart_pending_paypal');
          // Clean URL params to prevent re-capture on refresh
          window.history.replaceState({}, '', '/order-confirmation');
        }
      }.bind(this))
      .catch(function(err) {
        console.log('PayPal capture error:', err);
        this.setState({ loading: false, error: 'Something went wrong processing your payment. Please check your orders page.' });
      }.bind(this));
  }

  render() {
    // Loading state — PayPal capture in progress
    if (this.state.loading) {
      return (
        <div>
          <Helmet>
            <title>Processing Payment... | Primal Marketplace</title>
          </Helmet>
          <div className="tw-max-w-md tw-mx-auto tw-px-4 tw-py-12 tw-text-center">
            <div className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#2a2a2a] tw-p-8 tw-shadow-sm">
              <div className="tw-flex tw-justify-center tw-mb-5">
                <i className="fa fa-spinner fa-spin tw-text-4xl" style={{ color: '#c9a84c' }} />
              </div>
              <h1 className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">
                Processing Payment...
              </h1>
              <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-0">
                Please wait while we confirm your PayPal payment
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (this.state.error) {
      return (
        <div>
          <Helmet>
            <title>Payment Issue | Primal Marketplace</title>
          </Helmet>
          <div className="tw-max-w-md tw-mx-auto tw-px-4 tw-py-12 tw-text-center">
            <div className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#2a2a2a] tw-p-8 tw-shadow-sm">
              <div className="tw-flex tw-justify-center tw-mb-5">
                <i className="fa fa-exclamation-circle tw-text-4xl tw-text-red-500" />
              </div>
              <h1 className="tw-text-xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">
                Payment Issue
              </h1>
              <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-6">
                {this.state.error}
              </p>
              <Link
                to="/orders"
                className="tw-block tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-no-underline tw-transition-all hover:tw-shadow-md tw-text-black tw-bg-brand-gold hover:tw-bg-brand-gold-dark"
              >
                View Orders
              </Link>
            </div>
          </div>
        </div>
      );
    }

    // Get order data from various sources
    var state = this.state.paypalOrder; // From PayPal AJAX capture

    // Fallback: Stripe flow passes state via history.push
    if (!state) {
      state = this.props.location && this.props.location.state;
    }

    // Fallback: read from localStorage (legacy)
    if (!state) {
      try {
        var stored = localStorage.getItem('paypal_order');
        if (stored) {
          state = JSON.parse(stored);
          localStorage.removeItem('paypal_order');
        }
      } catch (e) {}
    }

    var hasDetails = !!state;
    var isCart = state && state.cartPurchase;

    // Guard: if no order data available, show generic success
    if (!hasDetails) {
      return (
        <div>
          <Helmet><title>Order Confirmed | Primal Marketplace</title></Helmet>
          <div className="tw-max-w-md tw-mx-auto tw-px-4 tw-py-12 tw-text-center">
            <div className="tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#2a2a2a] tw-p-8 tw-shadow-sm">
              <div className="tw-flex tw-justify-center tw-mb-5">
                <div className="tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: '#22c55e' }}>
                  <i className="fa fa-check tw-text-white tw-text-2xl" />
                </div>
              </div>
              <h1 className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">Order Placed!</h1>
              <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-6">Your order has been received. Check your orders page for details.</p>
              <Link to="/orders" className="tw-block tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-no-underline tw-transition-all hover:tw-shadow-md" style={{ backgroundColor: '#c9a84c', color: '#000' }}>
                View Orders
              </Link>
            </div>
          </div>
        </div>
      );
    }

    var image = state ? (resolveCardImage(state.image) || null) : null;

    var BRAND_MAP = {visa:'fa-cc-visa',mastercard:'fa-cc-mastercard',amex:'fa-cc-amex',discover:'fa-cc-discover',diners:'fa-cc-diners-club',jcb:'fa-cc-jcb'};

    // Build confirmation number — prefer transactionId (MongoDB _id) to match OrderHistory
    var rawOrderId = state ? (state.transactionId || state.orderId || state.chargeId || '') : '';
    var confirmationNum = rawOrderId ? rawOrderId.slice(-8).toUpperCase() : null;

    // Build item list for rendering (shared between single + cart)
    var renderItems = [];
    if (hasDetails && isCart && state.items) {
      renderItems = state.items;
    } else if (hasDetails && !isCart && state.title) {
      renderItems = [{ title: state.title, image: state.image, price: state.price, quantity: 1, postId: state.productId, condition: state.condition, rarity: state.rarity, foil: state.foil }];
    }

    // Group by seller
    var sellerGroups = {};
    var sellerOrder = [];
    renderItems.forEach(function(item) {
      var key = item.sellerId || item.sellerName || '_single';
      if (!sellerGroups[key]) {
        sellerGroups[key] = { sellerName: item.sellerName, items: [] };
        sellerOrder.push(key);
      }
      sellerGroups[key].items.push(item);
    });

    // Totals
    var totalSubtotal = isCart ? Number(state.subtotal || 0) : Number(state.price || 0);
    var totalShipping = isCart ? Number(state.shippingTotal || 0) : Number(state.shippingCost || 0);
    var totalTracking = Number(state.trackingUpgradeFee || 0);
    var totalTxFee = Number(state.transactionFee || 0);
    var totalTax = Number(state.taxAmount || 0);
    var totalGrand = state.grandTotal ? Number(state.grandTotal) : (totalSubtotal + totalShipping + totalTracking + totalTxFee + totalTax);

    var hasAddr = state && state.shippingAddress && state.shippingAddress.street1;
    var hasPay = state && state.paymentInfo;

    return (
            <div>
              <Helmet>
                <title>Order Confirmed | Primal Marketplace</title>
              </Helmet>

              <style>{`
                @keyframes ocScale { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
                @keyframes ocDraw { 0% { stroke-dashoffset: 50; } 100% { stroke-dashoffset: 0; } }
                @keyframes ocFadeUp { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
                .oc-circle { animation: ocScale 0.5s ease-out forwards; }
                .oc-check { stroke-dasharray: 50; stroke-dashoffset: 50; animation: ocDraw 0.4s ease-out 0.35s forwards; }
                .oc-fade { animation: ocFadeUp 0.4s ease-out 0.2s both; }
                .oc-fade-2 { animation: ocFadeUp 0.4s ease-out 0.4s both; }
                .oc-fade-3 { animation: ocFadeUp 0.4s ease-out 0.55s both; }
                .oc-card-bg { background: #fff; border: 1px solid #e5e5e5; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
                .tw-dark .oc-card-bg { background: #141414; border-color: #2a2a2a; }
                .oc-avatar { background: #f0f0f0; }
                .tw-dark .oc-avatar { background: #222; }
                .oc-avatar-icon { color: #aaa; }
                .tw-dark .oc-avatar-icon { color: #666; }
                .oc-thumb { background: #fafafa; border: 1px solid #eee; }
                .tw-dark .oc-thumb { background: #0f0f0f; border-color: #2a2a2a; }
                .oc-badge { background: #f0f0f0; }
                .tw-dark .oc-badge { background: #222; }
                .oc-price { color: #666; }
                .tw-dark .oc-price { color: #aaa; }
                .oc-divider { border-bottom: 1px dashed #eee; }
                .tw-dark .oc-divider { border-color: #262626; }
                .oc-totals { background: #fafafa; border-top: 1px solid #f0f0f0; }
                .tw-dark .oc-totals { background: #0f0f0f; border-color: #222; }
                .oc-total-line { border-top: 1px solid #e8e8e8; }
                .tw-dark .oc-total-line { border-color: #222; }
                .oc-section-border { border-top: 1px solid #f0f0f0; }
                .tw-dark .oc-section-border { border-color: #222; }
                .oc-meta-label { color: #bbb; }
                .tw-dark .oc-meta-label { color: #888; }
                .oc-card-icon { color: #444; }
                .tw-dark .oc-card-icon { color: #ccc; }
                .oc-protect { background: #f7f5ef; border-top: 1px solid #f0f0f0; }
                .tw-dark .oc-protect { background: #111; border-color: #222; }
                .oc-protect-text { color: #777; }
                .tw-dark .oc-protect-text { color: #aaa; }

              `}</style>

              <div className="tw-max-w-md tw-mx-auto tw-px-4 tw-py-5">

                {/* Hero — inline checkmark + text + price */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4 oc-fade">
                  <svg width="36" height="36" viewBox="0 0 64 64" className="oc-circle tw-flex-shrink-0">
                    <circle cx="32" cy="32" r="30" fill="#22c55e" />
                    <path d="M18,32 L27,41 L46,22" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="oc-check" />
                  </svg>
                  <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0" style={{ fontFamily: "'Lora', serif" }}>
                      Order Confirmed
                    </h1>
                    {confirmationNum && (
                      <p className="tw-text-sm tw-font-semibold tw-mb-0 tw-tabular-nums" style={{ color: '#c9a84c' }}>
                        #{confirmationNum}
                      </p>
                    )}
                  </div>
                </div>

                {/* Receipt card */}
                {hasDetails && (
                  <div className="tw-rounded-xl tw-overflow-hidden tw-mb-4 oc-fade-2 oc-card-bg">

                    {/* Items — compact receipt rows */}
                    <div className="tw-px-4 tw-pt-3 tw-pb-1">
                      {sellerOrder.map(function(key, gi) {
                        var group = sellerGroups[key];
                        return (
                          <div key={key}>
                            {group.sellerName && (
                              <a href={'/shop/' + group.sellerName} className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5 tw-no-underline tw-group">
                                <span className="tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 oc-avatar">
                                  <i className="fa fa-user oc-avatar-icon" style={{ fontSize: 8 }} />
                                </span>
                                <span className="tw-text-xs tw-font-semibold tw-text-[#666] dark:tw-text-gray-400 group-hover:tw-text-brand-gold tw-transition-colors">
                                  {group.sellerName}
                                </span>
                              </a>
                            )}
                            {group.items.map(function(item, idx) {
                              var itemImg = resolveCardImage(item.image) || resolveCardImage(item.cardImage) || null;
                              return (
                                <div key={idx} className="tw-flex tw-items-center tw-gap-3 tw-py-1.5">
                                  {itemImg ? (
                                    <img src={itemImg} alt={item.title} className="tw-flex-shrink-0 tw-rounded-lg tw-object-contain oc-thumb" style={{ width: 42, height: 42 }} />
                                  ) : (
                                    <div className="tw-flex-shrink-0 tw-rounded-lg tw-flex tw-items-center tw-justify-center oc-thumb" style={{ width: 42, height: 42 }}>
                                      <i className="fa fa-image tw-text-sm tw-text-[#ccc] dark:tw-text-[#444]" />
                                    </div>
                                  )}
                                  <div className="tw-flex-1 tw-min-w-0">
                                    <span className="tw-text-sm tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-block">{item.title}{(item.quantity || 1) > 1 ? ' x' + item.quantity : ''}</span>
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-mt-0.5">
                                      {item.condition && (
                                        <span className="tw-text-[10px] tw-font-medium tw-px-1 tw-rounded oc-badge" style={{ color: '#888' }}>{item.condition}</span>
                                      )}
                                      {item.rarity && <span style={{ transform: 'scale(0.85)', transformOrigin: 'left center', display: 'inline-flex' }}>{renderRarityBadge(item.rarity, null)}</span>}
                                      {item.foil && (
                                        <span className="tw-text-[10px] tw-font-semibold tw-px-1 tw-rounded" style={{ background: 'linear-gradient(135deg, #c9a84c, #f0d78c, #c9a84c)', color: '#1a1a1a' }}>Foil</span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="tw-text-sm tw-font-medium tw-tabular-nums tw-flex-shrink-0 oc-price">
                                    ${((Number(item.price) || 0) * (item.quantity || 1)).toFixed(2)}
                                  </span>
                                </div>
                              );
                            })}
                            {gi < sellerOrder.length - 1 && (
                              <div className="tw-my-1 oc-divider" />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Totals — compact receipt style */}
                    <div className="tw-px-4 tw-py-3 oc-totals">
                      <div className="tw-flex tw-justify-between tw-mb-1">
                        <span className="tw-text-xs" style={{ color: '#999' }}>Subtotal</span>
                        <span className="tw-text-xs tw-tabular-nums" style={{ color: '#999' }}>${totalSubtotal.toFixed(2)}</span>
                      </div>
                      <div className="tw-flex tw-justify-between tw-mb-1">
                        <span className="tw-text-xs" style={{ color: '#999' }}>Shipping</span>
                        {totalShipping > 0
                          ? <span className="tw-text-xs tw-tabular-nums" style={{ color: '#999' }}>${totalShipping.toFixed(2)}</span>
                          : <span className="tw-text-xs tw-font-medium" style={{ color: '#22c55e' }}>Free</span>
                        }
                      </div>
                      {totalTracking > 0 && (
                        <div className="tw-flex tw-justify-between tw-mb-1">
                          <span className="tw-text-xs" style={{ color: '#999' }}>Tracking</span>
                          <span className="tw-text-xs tw-tabular-nums" style={{ color: '#999' }}>${totalTracking.toFixed(2)}</span>
                        </div>
                      )}
                      {totalTxFee > 0 && (
                        <div className="tw-flex tw-justify-between tw-mb-1">
                          <span className="tw-text-xs" style={{ color: '#999' }}>Transaction Fee</span>
                          <span className="tw-text-xs tw-tabular-nums" style={{ color: '#999' }}>${totalTxFee.toFixed(2)}</span>
                        </div>
                      )}
                      {totalTax > 0 && (
                        <div className="tw-flex tw-justify-between tw-mb-1">
                          <span className="tw-text-xs" style={{ color: '#999' }}>Tax</span>
                          <span className="tw-text-xs tw-tabular-nums" style={{ color: '#999' }}>${totalTax.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="tw-flex tw-justify-between tw-pt-2 tw-mt-1.5 oc-total-line">
                        <span className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">Total</span>
                        <span className="tw-text-base tw-font-bold tw-tabular-nums" style={{ color: '#c9a84c' }}>${totalGrand.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Shipping & Payment — inline row */}
                    {(hasAddr || hasPay) && (
                      <div className="tw-px-4 tw-py-3 tw-flex tw-items-start tw-gap-4 oc-section-border">
                        {hasAddr && (
                          <div className="tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
                              <i className="fa fa-map-marker" style={{ color: '#c9a84c', fontSize: 11 }} />
                              <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider oc-meta-label">Ship to</span>
                            </div>
                            <p className="tw-text-xs tw-text-[#1a1a1a] dark:tw-text-gray-300 tw-mb-0 tw-leading-snug">
                              {state.shippingTo && <span className="tw-font-medium">{state.shippingTo} &middot; </span>}
                              <span style={{ color: '#999' }}>
                                {state.shippingAddress.street1}{state.shippingAddress.street2 ? ', ' + state.shippingAddress.street2 : ''}, {state.shippingAddress.city}{state.shippingAddress.state ? ', ' + state.shippingAddress.state : ''} {state.shippingAddress.zip}
                              </span>
                            </p>
                          </div>
                        )}
                        {hasPay && (
                          <div className="tw-flex-shrink-0">
                            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
                              <i className="fa fa-lock" style={{ color: '#c9a84c', fontSize: 11 }} />
                              <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider oc-meta-label">Paid with</span>
                            </div>
                            {state.paymentInfo.method === 'paypal' ? (
                              <div className="tw-flex tw-items-center tw-gap-1.5">
                                <i className="fa fa-paypal tw-text-base" style={{ color: '#003087' }} />
                                <span className="tw-text-xs tw-font-medium tw-text-[#1a1a1a] dark:tw-text-gray-300">PayPal</span>
                              </div>
                            ) : (
                              <div className="tw-flex tw-items-center tw-gap-1.5">
                                <i className={'fa ' + (BRAND_MAP[state.paymentInfo.brand] || 'fa-credit-card') + ' tw-text-base oc-card-icon'} />
                                <span className="tw-text-xs tw-font-medium tw-text-[#1a1a1a] dark:tw-text-gray-300">
                                  {state.paymentInfo.brand ? state.paymentInfo.brand.charAt(0).toUpperCase() + state.paymentInfo.brand.slice(1) : 'Card'}
                                  {state.paymentInfo.last4 ? ' ····' + state.paymentInfo.last4 : ''}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Buyer protection strip */}
                    <a href="/more#buyer-protection" target="_blank" rel="noopener noreferrer" className="tw-px-4 tw-py-2 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-no-underline hover:tw-opacity-80 tw-transition-opacity tw-block oc-protect">
                      <i className="fa fa-shield" style={{ color: '#c9a84c', fontSize: 11 }} />
                      <span className="tw-text-[11px] tw-font-medium oc-protect-text">
                        Primal Buyer Protection
                      </span>
                    </a>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="oc-fade-3">
                  <AuthModalContext.Consumer>
                    {({ openAuthModal }) => (
                      <div className="tw-flex tw-flex-col tw-gap-3">
                        {localStorage.getItem('primal_cognito_token') ? (
                          <Link
                            to="/orders"
                            className="tw-block tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-no-underline tw-transition-colors tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black"
                          >
                            View Orders
                          </Link>
                        ) : (
                          <>
                            <button
                              onClick={function() { openAuthModal('signup'); }}
                              className="tw-block tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-no-underline tw-transition-colors tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-border-0 tw-cursor-pointer"
                            >
                              Create Account to Track Orders
                            </button>
                            <button
                              onClick={function() { openAuthModal('login'); }}
                              className="tw-block tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-no-underline tw-transition-colors tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#666] dark:tw-text-gray-400 hover:tw-border-brand-gold hover:tw-text-brand-gold tw-bg-transparent tw-cursor-pointer"
                            >
                              Already have an account? Log in
                            </button>
                          </>
                        )}
                        <Link
                          to="/browse"
                          className="tw-block tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-no-underline tw-transition-colors tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#666] dark:tw-text-gray-400 hover:tw-border-brand-gold hover:tw-text-brand-gold tw-bg-transparent"
                        >
                          Continue Shopping
                        </Link>
                      </div>
                    )}
                  </AuthModalContext.Consumer>
                </div>
              </div>
            </div>
    );
  }
}

export default OrderConfirmation;
